import React, { forwardRef } from "react";
import { Search } from "lucide-react";

/**
 * Phase 2 — Input
 *
 * Soft-shadow form input replacing legacy `border rounded-sm bg-input`.
 * Use for text fields, search bars, numeric inputs, and the new
 * confirm-phrase input on Catalog/CSV Import.
 *
 *   <Input placeholder="Search products" />
 *   <Input variant="search" placeholder="Find SKU…" onChange={…} />
 *   <Input type="number" min={0} value={limit} onChange={…} />
 */
const BASE =
  "w-full h-11 px-3 rounded-button bg-paper-2 border border-border/60 shadow-rest " +
  "text-sm placeholder:text-ink-3 " +
  "focus:shadow-raised focus:outline-none focus:border-brand/30 " +
  "disabled:opacity-50 disabled:cursor-not-allowed " +
  "transition-shadow duration-150 ease-out";

const Input = forwardRef(function Input(
  {
    variant,
    className = "",
    leadingIcon,
    ...rest
  },
  ref,
) {
  if (variant === "search") {
    return (
      <div className={`relative ${className}`}>
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none"
        />
        <input
          ref={ref}
          {...rest}
          className={`${BASE} pl-9`}
        />
      </div>
    );
  }

  if (leadingIcon) {
    return (
      <div className={`relative ${className}`}>
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none">
          {leadingIcon}
        </span>
        <input ref={ref} {...rest} className={`${BASE} pl-9`} />
      </div>
    );
  }

  return <input ref={ref} {...rest} className={`${BASE} ${className}`} />;
});

export default Input;

/**
 * Native <select> wrapper with the same visual treatment.
 *
 *   <Select value={role} onChange={…}>
 *     <option value="employee">Employee</option>
 *     ...
 *   </Select>
 */
export const Select = forwardRef(function Select(
  { className = "", children, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      {...rest}
      className={`${BASE} pr-8 appearance-none cursor-pointer bg-no-repeat bg-right ${className}`}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238A8A8A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
        backgroundPosition: "right 12px center",
      }}
    >
      {children}
    </select>
  );
});
